import { Badge, Banner, Button, Select } from '@cloudflare/kumo';
import { useMemo } from 'react';
import { EmptyState } from '../ui/EmptyState';
import { ICONS } from '../ui/icons';
import { ChannelBadge } from './ChannelBadge';
import { MessageBubble } from './MessageBubble';
import { ReplyInput } from './ReplyInput';
import { NotesPanel } from './NotesPanel';

type Conversation = {
  id: string;
  channel: 'whatsapp' | 'instagram' | 'widget';
  status: 'OPEN' | 'AI' | 'HUMAN' | 'CLOSED';
  summary: string | null;
  summaryGeneratedAt: string | null;
  summaryModel: string | null;
  lastMessageAt: string;
  lastInboundAt: string | null;
};
type Contact = { id: string; nome: string; telefone: string | null; email: string | null; tags: string[] } | null;
type Assignee = { id: string; nome: string } | null;
type Message = {
  id: string;
  kind: 'INBOUND' | 'OUTBOUND' | 'NOTE' | 'SYSTEM' | 'AI';
  direction: 'IN' | 'OUT';
  content: string;
  mediaType: string | null;
  mediaUrl: string | null;
  authorId: string | null;
  author: string | null;
  createdAt: string;
};

type Props = {
  conversation: Conversation;
  contact: Contact;
  assignee: Assignee;
  messages: Message[];
  windowInfo: { open: boolean; closesAt: string | null };
  agents: Array<{ id: string; nome: string }>;
  currentUserId: string;
  busy?: boolean;
  onReply: (text: string) => Promise<void> | void;
  onAddNote: (text: string) => Promise<void> | void;
  onClaim: () => Promise<void> | void;
  onAssign: (userId: string | null) => Promise<void> | void;
  onSetStatus: (s: 'OPEN' | 'AI' | 'HUMAN' | 'CLOSED') => Promise<void> | void;
  onSuggest: () => Promise<void> | void;
  onSummarize: () => Promise<void> | void;
  onBack?: () => void;
};

/** Valor sentinela da opção "sem responsável" (o `Select` do Cloudscape não
 *  trata string vazia como uma opção válida). */
const NO_ASSIGNEE = '__none__';

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Aberta' },
  { value: 'AI', label: 'IA' },
  { value: 'HUMAN', label: 'Equipe' },
  { value: 'CLOSED', label: 'Encerrada' },
];

export function ConversationDetail({
  conversation, contact, assignee, messages, windowInfo, agents, currentUserId, busy,
  onReply, onAddNote, onClaim, onAssign, onSetStatus, onSuggest, onSummarize, onBack,
}: Props) {
  const isMine = assignee?.id === currentUserId;
  const noteMessages = useMemo(() => messages.filter((m) => m.kind === 'NOTE'), [messages]);
  const chatMessages = useMemo(() => messages.filter((m) => m.kind !== 'NOTE'), [messages]);

  const statusOption = STATUS_OPTIONS.find((o) => o.value === conversation.status) ?? null;
  const agentOptions = [
    { value: NO_ASSIGNEE, label: 'Sem responsável' },
    ...agents.map((a) => ({ value: a.id, label: a.nome })),
  ];
  const assigneeOption = agentOptions.find((o) => o.value === (assignee?.id ?? NO_ASSIGNEE)) ?? null;

  return (
    <section className="inbox-detail" role="region" aria-label="Detalhes da conversa">
      <header className="inbox-detail__header">
        <div className="inbox-detail__head-left">
          {onBack && (
            <Button variant="ghost" shape="square" icon={ICONS.ArrowLeft} aria-label="Voltar para a lista" onClick={onBack} className="inbox-detail__back" />
          )}
          <ChannelBadge channel={conversation.channel} />
          <div className="inbox-detail__contact">
            <h2 className="text-xl font-semibold" style={{ margin: 0, fontSize: 18, lineHeight: '24px' }}>{contact?.nome ?? 'Visitante sem nome'}</h2>
            <span className="text-xs text-kumo-subtle inbox-detail__contact-meta">
              {contact?.telefone && <span>{contact.telefone}</span>}
              {contact?.email && <span> · {contact.email}</span>}
              {assignee && <span> · com {assignee.nome}</span>}
              {!assignee && <span className="inbox-detail__unassigned"> · sem responsável</span>}
            </span>
          </div>
        </div>
        <div className="inbox-detail__head-right">
          <Select value={(statusOption)?.value ?? null} items={STATUS_OPTIONS} onValueChange={(value: string | null) => {
              const v = value;
              if (v) onSetStatus(v as 'OPEN' | 'AI' | 'HUMAN' | 'CLOSED');
            }} aria-label="Status da conversa" data-testid="inbox-status" disabled={busy} />
          <Select value={(assigneeOption)?.value ?? null} items={agentOptions} onValueChange={(value: string | null) => {
              const v = value;
              onAssign(!v || v === NO_ASSIGNEE ? null : v);
            }} aria-label="Atribuir a" data-testid="inbox-assign" disabled={busy} />
          {!isMine && (
            <Button variant="primary" onClick={onClaim} disabled={busy} data-testid="inbox-claim">
              Pegar conversa
            </Button>
          )}
          <Button variant="secondary" icon={ICONS.Sparkle} onClick={onSummarize} disabled={busy} data-testid="inbox-summarize" title={'Resumir com IA'}>
            Resumir
          </Button>
        </div>
      </header>

      {conversation.summary && (
        <Banner variant="default" className="inbox-detail__banner" data-testid="inbox-summary" description={<>
          <strong>Resumo:</strong> {conversation.summary}
          {conversation.summaryModel && <span> · modelo {conversation.summaryModel}</span>}
        </>} />
      )}

      {!windowInfo.open && (
        <Banner variant="alert" className="inbox-detail__banner" data-testid="inbox-window-warn" description={<>
          Janela de 24 h fechada — só envio de template aprovado.
        </>} />
      )}

      {/* role="log" + aria-live: leitor de tela anuncia só as mensagens novas. A key
          remonta a região ao trocar de conversa, para não ler o histórico inteiro. */}
      <div
        key={conversation.id}
        className="inbox-detail__timeline"
        data-testid="inbox-timeline"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label="Mensagens da conversa"
        tabIndex={0}
      >
        {chatMessages.length === 0 && (
          <EmptyState
            title="Conversa vazia"
            description="Envie a primeira mensagem para iniciar a conversa."
            data-testid="inbox-detail-empty"
          />
        )}
        {chatMessages.map((m) => <MessageBubble key={m.id} message={m} />)}
      </div>

      <ReplyInput
        onSend={onReply}
        onSuggest={onSuggest}
        disabled={busy || conversation.status === 'CLOSED'}
        contextName={contact?.nome}
      />

      <details className="inbox-detail__notes" data-testid="inbox-notes-section">
        <summary>
          <span className="font-semibold">Notas internas</span>
          <Badge variant="neutral">{noteMessages.length}</Badge>
          <span className="text-xs text-kumo-subtle">só a equipe vê</span>
        </summary>
        <div className="inbox-detail__notes-body">
          {noteMessages.length === 0 && <span className="text-xs text-kumo-subtle">Sem notas ainda.</span>}
          {noteMessages.map((m) => <MessageBubble key={m.id} message={m} />)}
          <NotesPanel onAdd={onAddNote} disabled={busy} />
        </div>
      </details>
    </section>
  );
}
